import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { InsiderTrade } from "@/lib/types";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Normalize a person's name for display. SEC Form 4 reporters arrive in
 * "LASTNAME FIRSTNAME M" ALL CAPS — convert to Title Case while keeping
 * common suffixes (JR, SR, III, MD, …) in uppercase and single-letter
 * initials capitalized. If the input already has any lowercase letters,
 * it's returned unchanged.
 */
const NAME_SUFFIXES = new Set([
  'JR', 'SR', 'II', 'III', 'IV', 'V', 'VI', 'VII',
  'MD', 'PHD', 'CFA', 'CPA', 'ESQ', 'DDS', 'DO',
]);
export const formatPersonName = (name: string | null | undefined): string => {
  if (!name) return '';
  if (/[a-z]/.test(name)) return name; // already mixed-case
  return name
    .toLowerCase()
    .split(/(\s+|-)/)
    .map((tok) => {
      if (!tok || /^\s+$/.test(tok) || tok === '-') return tok;
      const upper = tok.toUpperCase();
      if (NAME_SUFFIXES.has(upper.replace(/\.$/, ''))) return upper;
      if (tok.length === 1) return upper;
      if (tok.startsWith("o'") && tok.length > 2) {
        return "O'" + tok[2].toUpperCase() + tok.slice(3);
      }
      if (tok.startsWith('mc') && tok.length > 2) {
        return 'Mc' + tok[2].toUpperCase() + tok.slice(3);
      }
      if (tok.startsWith('mac') && tok.length > 3) {
        return 'Mac' + tok[3].toUpperCase() + tok.slice(4);
      }
      return tok[0].toUpperCase() + tok.slice(1);
    })
    .join('');
};

export function normalizeTradeRow(t: any): InsiderTrade {
  return {
    id: t.id,
    filer_name: formatPersonName(t.filer_name) || 'Unknown',
    filer_title: t.filer_title || 'Insider',
    ticker: t.ticker || 'N/A',
    company_name: t.company_name || 'Unknown',
    transaction_type: t.transaction_type === 'sell' ? 'sell' : 'buy',
    shares: Number(t.shares) || 0,
    price_per_share: Number(t.price_per_share) || 0,
    total_value: Number(t.total_value) || 0,
    filing_date: t.filing_date || '',
    transaction_date: t.transaction_date || undefined,
    accession_number: String(t.accession_number || ''),
    created_at: t.created_at || new Date().toISOString(),
    ai_summary: t.ai_summary || undefined,
    ai_summary_generated_at: t.ai_summary_generated_at || undefined,
    follower_entry_price: t.follower_entry_price != null ? Number(t.follower_entry_price) : null,
  };
}


/** Parse a date string. Accepts both 'YYYY-MM-DD' and full ISO timestamps. */
const parseDate = (dateStr: string): Date => {
  // If it's already a full timestamp (contains 'T' or space-separated time), parse directly.
  if (/[T\s]\d{2}:/.test(dateStr)) return new Date(dateStr);
  // Otherwise treat as date-only and anchor to local midnight.
  return new Date(dateStr + 'T00:00:00');
};

/** Resolve any incoming date-like value to a valid Date, or null. */
const toValidDate = (input: string | Date | null | undefined): Date | null => {
  if (input == null || input === '') return null;
  const d = input instanceof Date ? input : parseDate(input);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Canonical data-column date format: "MM/DD/YYYY".
 * Use for filing_date, transaction_date, disclosure_date, last_trade_date,
 * last_calculated, and any column that displays a specific trade or filing date.
 * Returns "—" for null/undefined/invalid inputs.
 */
export const formatDateMMDDYYYY = (input: string | Date | null | undefined): string => {
  const d = toValidDate(input);
  if (!d) return '—';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

/**
 * Prose / marketing date format: "MMM D, YYYY" (e.g. "Mar 5, 2026").
 * Use for marketing copy, page headers, "Last updated" footers, prose context.
 * Returns "—" for null/undefined/invalid inputs.
 */
export const formatDateShort = (input: string | Date | null | undefined): string => {
  const d = toValidDate(input);
  if (!d) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/** Compact relative time: "just now", "5m ago", "3h ago", "2d ago" */
export const getRelativeTime = (dateStr: string) => {
  const diffMs = Date.now() - parseDate(dateStr).getTime();
  if (!Number.isFinite(diffMs)) return '';
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  return diffD === 1 ? '1d ago' : `${diffD}d ago`;
};

/** Verbose relative time: "today", "1 day ago", "3 months ago" */
export const getRelativeTimeVerbose = (dateStr: string) => {
  const diffMs = Date.now() - parseDate(dateStr).getTime();
  if (!Number.isFinite(diffMs)) return '';
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffDays <= 0) return 'today';
  if (diffDays === 1) return '1 day ago';
  if (diffDays < 30) return `${diffDays} days ago`;
  const diffMonths = Math.floor(diffDays / 30);
  return diffMonths === 1 ? '1 month ago' : `${diffMonths} months ago`;
};

/** Compact currency: $1.1M, $50K, $123 */
export const formatCurrency = (value: number) => {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
};

/** Detailed currency: $1.10M, $50.1K, $123.45 */
export const formatCurrencyDetail = (value: number) => {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
  return `$${value.toFixed(2)}`;
};

/**
 * Canonical directional color for numeric percentage returns
 * (peak return, win-rate-relative, P/L %, alpha vs SPY, etc.).
 *
 * Muted by design — the new editorial aesthetic favors quiet
 * semantic tone over fully saturated greens/reds. Use this everywhere
 * a NUMERIC PERCENTAGE is rendered. Do NOT use it for buy/sell
 * action labels — those keep `text-buy` / `text-sell`.
 */
export const getReturnColor = (value: number | null | undefined): string => {
  if (value == null || Number.isNaN(value)) return 'text-foreground/60';
  if (value > 0) return 'text-emerald-400/80';
  if (value < 0) return 'text-red-400/80';
  return 'text-foreground/60';
};

/**
 * Outlier cap for percent returns / alpha display.
 * Values above +200% render as "+200%+" and below -200% as "-200%+".
 * Display layer only — does not alter underlying data.
 *
 * @param n     Raw percent value (already in percent, e.g. 268 = +268%)
 * @param opts.decimals   Decimals to show inside the cap range (default 1)
 * @param opts.withSign   Prefix '+' on non-negative values (default true)
 */
export const formatCappedPct = (
  n: number | null | undefined,
  opts: { decimals?: number; withSign?: boolean } = {}
): string => {
  if (n == null || Number.isNaN(n)) return '—';
  const { decimals = 1, withSign = true } = opts;
  if (n > 200) return '+200%+';
  if (n < -200) return '-200%+';
  const sign = withSign && n >= 0 ? '+' : '';
  return `${sign}${n.toFixed(decimals)}%`;
};

/**
 * STOCK Act amount-range midpoint estimator.
 *
 * Mirrors the canonical implementation in
 * supabase/functions/update-congress-outcomes/index.ts (parseAmountMidpoint).
 * Used by the firehose / Congress cards to derive a sortable estimated value
 * when no congress_signal_outcomes row exists yet.
 *
 * Returns null for unparseable inputs (e.g. "< $1K", "$50M+"). Callers should
 * fall back to the raw range string in that case.
 */
export const parseCongressAmountMidpoint = (rangeRaw: string | null | undefined): number | null => {
  if (!rangeRaw) return null;
  const cleaned = rangeRaw.replace(/[$,]/g, '').trim();
  const m = cleaned.match(/(\d+)\s*-\s*(\d+)/);
  if (!m) return null;
  const lo = Number(m[1]);
  const hi = Number(m[2]);
  if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi < lo) return null;
  return (lo + hi) / 2;
};

/**
 * Derive a plain-English confidence tier for an insider's statistical metrics,
 * based on how many settled 90-day windows (settled_count) back their numbers.
 */
export function getInsiderConfidenceTier(settledCount: number | null | undefined): string {
  const n = settledCount ?? 0;
  if (n >= 25) return 'high confidence';
  if (n >= 10) return 'medium confidence';
  if (n >= 3)  return 'low confidence';
  return 'insufficient data';
}
